import { useCallback, useEffect, useMemo, useState } from 'react'
import { ContactsToolbar } from './ContactsToolbar'
import { ContactsAdvancedFilters } from './ContactsAdvancedFilters'
import { ContactsTable, type ColumnDef, type ContactColumnKey } from './ContactsTable'
import { ContactsBulkBar } from './ContactsBulkBar'
import { Pagination } from '../shared/Pagination'
import { useContacts, EMPTY_FILTERS, CONTACTS_PAGE_SIZE, type ContactsFilters } from './useContacts'
import { getSetting } from '../../lib/settings'
import { listCustomFieldDefs } from '../../lib/outreach/api'
import type { CustomFieldDef } from '../../lib/outreach/types'
import { SavedViewsMenu } from './SavedViewsMenu'

const DEFAULT_COLS: ColumnDef[] = [
  { key: 'job_title', label: 'Title', visible: true },
  { key: 'primary_email', label: 'Email', visible: true },
  { key: 'primary_phone', label: 'Phone', visible: true },
  { key: 'lifecycle_stage', label: 'Stage', visible: true },
  { key: 'priority_score', label: 'Score', visible: true },
  { key: 'linkedin_url', label: 'LinkedIn', visible: true },
  { key: 'company_domain', label: 'Domain', visible: false },
  { key: 'country', label: 'Country', visible: false },
  { key: 'industry', label: 'Industry', visible: false },
  { key: 'last_contacted', label: 'Last Contact', visible: false },
]

function loadColConfig(): ColumnDef[] {
  try {
    const raw = getSetting('contacts_col_config', '')
    if (!raw) return DEFAULT_COLS
    const parsed = JSON.parse(raw) as ColumnDef[]
    if (!Array.isArray(parsed) || !parsed.length) return DEFAULT_COLS
    // Preserve known keys; ignore unknowns to avoid drift
    const known = new Set<ContactColumnKey>(DEFAULT_COLS.map((c) => c.key))
    return parsed.filter((c) => known.has(c.key))
  } catch {
    return DEFAULT_COLS
  }
}

export default function ContactsIsland() {
  const [filters, setFilters] = useState<ContactsFilters>(EMPTY_FILTERS)
  const [page, setPage] = useState(1)
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [customDefs, setCustomDefs] = useState<CustomFieldDef[]>([])

  // Custom fields are optional: if the lookup fails the list works without them.
  useEffect(() => {
    let cancelled = false
    listCustomFieldDefs('contact')
      .then((defs) => {
        if (!cancelled) setCustomDefs(defs)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [])

  const visibleColumns = useMemo(() => loadColConfig().filter((c) => c.visible), [])

  const { rows, ucMap, total, status, error, rotDays, rotCutoff, refetch } = useContacts({
    filters,
    page,
  })

  const patchFilters = useCallback((patch: Partial<ContactsFilters>) => {
    setFilters((f) => ({ ...f, ...patch }))
    setPage(1)
    setSelectedIds(new Set())
  }, [])

  const onSearchChange = useCallback((v: string) => patchFilters({ search: v }), [patchFilters])
  const onStageChange = useCallback((v: string) => patchFilters({ stage: v }), [patchFilters])
  const clearFilters = useCallback(() => {
    setFilters(EMPTY_FILTERS)
    setPage(1)
    setSelectedIds(new Set())
  }, [])

  const onToggleRow = useCallback((id: string, checked: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])
  const onToggleAll = useCallback(
    (checked: boolean) => {
      setSelectedIds((prev) => {
        const next = new Set(prev)
        if (checked) rows.forEach((r) => next.add(r.id))
        else rows.forEach((r) => next.delete(r.id))
        return next
      })
    },
    [rows],
  )
  const clearSelection = useCallback(() => setSelectedIds(new Set()), [])
  const isFiltered = page > 1 || JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS)

  return (
    <>
      <ContactsToolbar
        search={filters.search}
        stage={filters.stage}
        onSearchChange={onSearchChange}
        onStageChange={onStageChange}
        advancedOpen={advancedOpen}
        onToggleAdvanced={() => setAdvancedOpen((v) => !v)}
        extra={
          <SavedViewsMenu
            filters={filters}
            customDefs={customDefs}
            onApply={(next) => {
              setFilters(next)
              setPage(1)
              setSelectedIds(new Set())
            }}
          />
        }
      />
      <ContactsAdvancedFilters
        filters={filters}
        onChange={patchFilters}
        onClear={clearFilters}
        open={advancedOpen}
        customDefs={customDefs}
      />
      <ContactsTable
        rows={rows}
        ucMap={ucMap}
        status={status}
        error={error}
        rotDays={rotDays}
        rotCutoff={rotCutoff}
        selectedIds={selectedIds}
        onToggleRow={onToggleRow}
        onToggleAll={onToggleAll}
        visibleColumns={visibleColumns}
        isFiltered={isFiltered}
        onClearFilters={clearFilters}
      />
      <Pagination total={total} page={page} pageSize={CONTACTS_PAGE_SIZE} onPageChange={setPage} />
      <ContactsBulkBar
        selectedIds={[...selectedIds]}
        onClear={clearSelection}
        onChanged={refetch}
      />
    </>
  )
}
